import { useCallback, useState } from "react";
import type { CalendarFilters, CalendarStatusGroup } from "@dentacore/shared-types";

/**
 * **What the filter panel has hidden** (CF-D16) — one model for every filter:
 * per group, the values that are off. Nothing hidden is everything shown, so a
 * new doctor or a new category appears without anybody ticking it.
 *
 * Held for the visit to the calendar, not remembered: a filter left on from
 * yesterday is a booking somebody cannot find today.
 */
export type FilterGroup = "branchIds" | "providerIds" | "chairIds" | "types" | "statuses";

const NOTHING_HIDDEN = {
  branchIds: [] as string[],
  providerIds: [] as string[],
  chairIds: [] as string[],
  types: [] as string[],
  statuses: [] as CalendarStatusGroup[],
};

export interface CalendarFilterState {
  filters: CalendarFilters;
  /** A doctor's "Only my bookings" (CF-D11), when it applies. */
  onlyMe: boolean;
  setOnlyMe: (on: boolean) => void;
  isHidden: (group: FilterGroup, value: string) => boolean;
  toggle: (group: FilterGroup, value: string, shown: boolean) => void;
  /** Everything in the group but this one; the same again shows the whole group. */
  only: (group: FilterGroup, value: string, all: readonly string[]) => void;
  reset: (group: FilterGroup | "all") => void;
  /** Groups with something hidden, in panel order. */
  active: FilterGroup[];
  openSection: FilterGroup | null;
  setOpenSection: (group: FilterGroup | null) => void;
}

/**
 * @param me the signed-in clinician, whose combined view opens on their own
 *   bookings (CF-D11) — null for anyone who is not a provider, or with one
 *   location shown.
 */
export function useCalendarFilters(me: string | null): CalendarFilterState {
  const [hidden, setHidden] = useState(NOTHING_HIDDEN);
  const [onlyMe, setOnlyMe] = useState(true);
  // Which chip's dropdown is open (CZ-D7): none until one is pressed.
  const [openSection, setOpenSection] = useState<FilterGroup | null>(null);

  const isHidden = useCallback(
    (group: FilterGroup, value: string) => (hidden[group] as string[]).includes(value),
    [hidden],
  );

  const toggle = useCallback((group: FilterGroup, value: string, shown: boolean) => {
    setHidden((was) => {
      const list = was[group] as string[];
      const next = shown ? list.filter((item) => item !== value) : [...list, value];
      return { ...was, [group]: next };
    });
  }, []);

  const only = useCallback((group: FilterGroup, value: string, all: readonly string[]) => {
    setHidden((was) => {
      const list = was[group] as string[];
      const alreadyOnly =
        !list.includes(value) && all.every((item) => item === value || list.includes(item));
      return { ...was, [group]: alreadyOnly ? [] : all.filter((item) => item !== value) };
    });
  }, []);

  const reset = useCallback((group: FilterGroup | "all") => {
    if (group === "all") {
      setHidden(NOTHING_HIDDEN);
      setOnlyMe(false);
      return;
    }
    setHidden((was) => ({ ...was, [group]: [] }));
  }, []);

  const meApplies = me !== null && onlyMe;
  const filters: CalendarFilters = {
    ...hidden,
    onlyProviderId: meApplies ? me : null,
  };
  const active = (["branchIds", "providerIds", "chairIds", "types", "statuses"] as const).filter(
    (group) => hidden[group].length > 0,
  );

  return {
    filters,
    onlyMe: meApplies,
    setOnlyMe,
    isHidden,
    toggle,
    only,
    reset,
    active,
    openSection,
    setOpenSection,
  };
}
